<template>
  <div class="dashboard-container">
    <!-- ── Header ── -->
    <div class="dashboard-header mb-4">
      <el-row :gutter="20" type="flex" align="middle">
        <el-col :xs="24" :sm="12" :md="8">
          <div class="header-info">
            <h1 class="dashboard-title">
              <i class="mdi mdi-view-dashboard-outline"></i>
              {{ license.client || $t('dashboard.home.defaultBusinessName') }} {{ $t('dashboard.home.titleSuffix') }}
            </h1>
            <p class="dashboard-subtitle">{{ $t('dashboard.home.subtitle') }}</p>
          </div>
        </el-col>
        <el-col :xs="24" :sm="12" :md="16">
          <div class="header-filters">
            <el-form :inline="true" size="small" label-position="top">
              <el-form-item :label="$t('dashboard.home.filters.period')">
                <el-date-picker
                  v-model="dateRange"
                  type="daterange"
                  :range-separator="$t('dashboard.home.filters.to')"
                  :start-placeholder="$t('dashboard.home.filters.startDate')"
                  :end-placeholder="$t('dashboard.home.filters.endDate')"
                  format="MMM dd, yyyy"
                  value-format="yyyy-MM-dd"
                  :shortcuts="dateShortcuts"
                  @change="fetchDashboardData"
                />
              </el-form-item>
              <el-form-item :label="$t('dashboard.home.filters.store')">
                <el-select v-model="selectedStore" :placeholder="$t('dashboard.home.filters.allStores')" @change="fetchDashboardData">
                  <el-option :label="$t('dashboard.home.filters.allStores')" value="all" />
                  <el-option v-for="store in stores" :key="store.id" :label="store.name" :value="store.id" />
                </el-select>
              </el-form-item>
              <el-form-item label=" " class="mt-2">
                <el-button type="primary" size="mini" icon="el-icon-refresh" :loading="loading" @click="refreshDashboard">
                  {{ $t('button.refresh') }}
                </el-button>
              </el-form-item>
            </el-form>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- ── Loading ── -->
    <div v-if="loading" class="loading-container">
      <div class="loading-content">
        <i class="el-icon-loading"></i>
        <p>{{ $t('dashboard.home.loadingData') }}</p>
      </div>
    </div>

    <div v-else>
      <!-- ── Row 1 · KPI gradient cards ── -->
      <el-row :gutter="20" type="flex" class="mb-4">
        <el-col
          v-for="kpi in kpiCards"
          :key="kpi.title"
          :xs="24" :sm="12" :md="6"
          class="mb-3"
        >
          <div class="kpi-card" :class="kpi.class">
            <div class="kpi-watermark"><i :class="`mdi ${kpi.icon}`"></i></div>
            <div class="kpi-body">
              <div class="kpi-label">{{ kpi.title }}</div>
              <div class="kpi-amount">{{ kpi.value }}</div>
              <div class="kpi-trend-row">
                <span class="kpi-badge" :class="kpi.trend >= 0 ? 'badge-up' : 'badge-down'">
                  <i :class="kpi.trend >= 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                  {{ Math.abs(kpi.trend) }}%
                </span>
                <span class="kpi-vs">{{ $t('dashboard.home.vsLastPeriod') }}</span>
              </div>
            </div>
            <div class="kpi-spark">
              <line-chart
                v-if="kpi.sparklineData && kpi.sparklineData.datasets"
                :chart-data="kpi.sparklineData"
                :chart-options="sparklineOptions"
                :height="52"
              />
            </div>
          </div>
        </el-col>
      </el-row>

      <!-- ── Row 2 · Main trend chart + Category doughnut ── -->
      <el-row :gutter="20" type="flex" class="mb-4">
        <el-col :xs="24" :lg="16" class="mb-3">
          <el-card class="full-height-card">
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-chart-line"></i> {{ $t('dashboard.home.sections.revenueOrderTrends') }}</h3>
              <el-dropdown @command="changeSalesView">
                <el-button size="mini" type="text">
                  {{ getSalesViewLabel(salesViewType) }} <i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="hourly">{{ $t('dashboard.home.salesView.hourly') }}</el-dropdown-item>
                  <el-dropdown-item command="daily">{{ $t('dashboard.home.salesView.daily') }}</el-dropdown-item>
                  <el-dropdown-item command="weekly">{{ $t('dashboard.home.salesView.weekly') }}</el-dropdown-item>
                  <el-dropdown-item command="monthly">{{ $t('dashboard.home.salesView.monthly') }}</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
            <div class="chart-container" v-loading="trendsLoading">
              <line-chart :chart-data="salesPerformanceData" :chart-options="salesChartOptions" :height="280" />
            </div>
            <div class="chart-stats">
              <div class="stat-item">
                <span class="stat-label">{{ $t('dashboard.home.stats.totalOrders') }}</span>
                <span class="stat-value">{{ (dashboardData && dashboardData.sales_summary ? dashboardData.sales_summary.total_orders : 0) || 0 }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('dashboard.home.stats.avgTransaction') }}</span>
                <span class="stat-value">{{ formatCurrency(salesPerformance.average_transaction || 0) }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('dashboard.home.stats.profitMargin') }}</span>
                <span class="stat-value">{{ dashboardData && dashboardData.sales_summary ? dashboardData.sales_summary.profit_margin : 0 }}%</span>
              </div>
            </div>
          </el-card>
        </el-col>

        <el-col :xs="24" :md="8" class="mb-3">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-credit-card-outline"></i> {{ $t('dashboard.home.sections.paymentMethods') }}</h3>
            </div>
            <div class="payment-methods">
              <div v-for="method in paymentMethods" :key="method.name" class="payment-method">
                <div class="method-header">
                  <i :class="method.icon" />
                  <span class="method-name">{{ method.name }}</span>
                  <span class="method-pct">{{ method.percentage }}%</span>
                </div>
                <el-progress
                  :percentage="Math.min(100, Math.max(0, Number(method.percentage) || 0))"
                  :stroke-width="6"
                  :show-text="false"
                  :color="method.color"
                />
                <div class="method-meta">
                  <span>{{ $t('dashboard.home.transactions', { count: method.transactions }) }}</span>
                  <span>{{ formatCurrency(method.amount) }}</span>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>

      </el-row>

      <!-- ── Row 3 · Products · Payments · Quick Stats ── -->
      <el-row :gutter="20" type="flex" class="mb-4">
        <el-col :xs="24" :md="8" class="mb-3">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-package-variant"></i> {{ $t('dashboard.home.sections.topProducts') }}</h3>
              <el-tag size="mini" type="info">{{ getPeriodText() }}</el-tag>
            </div>
            <div class="product-list">
              <div v-for="(product, index) in topProducts" :key="index" class="product-item">
                <div class="product-rank" :class="'rank-' + (index + 1)">{{ index + 1 }}</div>
                <div class="product-info">
                  <div class="product-name">{{ product.name }}</div>
                  <div class="product-meta">
                    <span>{{ $t('dashboard.home.units', { count: product.units }) }}</span>
                    <span class="product-revenue">{{ formatCurrency(product.revenue) }}</span>
                  </div>
                  <el-progress
                    :percentage="Math.min(100, Math.max(0, Number(product.percentage) || 0))"
                    :stroke-width="4"
                    :show-text="false"
                    :color="getProgressColor(product.percentage)"
                  />
                </div>
                <div class="trend-badge" :class="product.trend > 0 ? 'up' : 'down'">
                  <i :class="product.trend > 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
                  {{ Math.abs(product.trend) }}%
                </div>
              </div>
            </div>
            <div class="view-all-link">
              <router-link to="/items">{{ $t('dashboard.home.actions.viewAllProducts') }} <i class="el-icon-arrow-right" /></router-link>
            </div>
          </el-card>
        </el-col>

        <el-col :xs="24" :lg="8" class="mb-3">
          <el-card class="full-height-card">
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-shape"></i> {{ $t('dashboard.home.sections.salesByCategory') }}</h3>
              <el-switch v-model="showCategoryValue" :active-text="$t('dashboard.home.value')" :inactive-text="$t('dashboard.home.unitPlural')" @change="updateCategoryChart" />
            </div>
            <div class="chart-container">
              <doughnut-chart :chart-data="categoryDistributionData" :chart-options="doughnutOptions" :height="220" />
            </div>
            <div class="category-legend">
              <div v-for="(cat, i) in categoryData" :key="i" class="legend-item">
                <span class="legend-dot" :style="{ backgroundColor: cat.color }" />
                <span class="legend-name">{{ cat.name }}</span>
                <span class="legend-value">{{ formatCurrency(cat.value) }}</span>
              </div>
            </div>
          </el-card>
        </el-col>

        <el-col :xs="24" :md="8" class="mb-3">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-speedometer"></i> {{ $t('dashboard.home.sections.quickStats') }}</h3>
            </div>
            <div class="quick-stats">
              <div v-for="(stat, i) in quickStats" :key="i" class="qstat-card">
                <div class="qstat-icon" :class="stat.color">
                  <i :class="stat.icon" />
                </div>
                <div class="qstat-info">
                  <div class="qstat-value">{{ stat.value }}</div>
                  <div class="qstat-label">{{ stat.title }}</div>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- ── Row 4 · Performers · Alerts · Activity ── -->
      <el-row :gutter="20" type="flex" class="mb-4">
        <el-col :xs="24" :md="8" class="mb-3">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-account-group"></i> {{ $t('dashboard.home.sections.topPerformers') }}</h3>
            </div>
            <div class="employee-list">
              <div v-for="(emp, i) in topPerformers" :key="i" class="employee-item">
                <div class="employee-rank">{{ i + 1 }}</div>
                <div class="employee-info">
                  <div class="employee-name">{{ emp.name }}</div>
                  <div class="employee-meta">
                    <span>{{ $t('dashboard.home.salesCount', { count: emp.transactions }) }}</span>
                    <span>{{ formatCurrency(emp.total_sales) }}</span>
                  </div>
                  <el-rate v-model="emp.rating" disabled show-score text-color="#ff9900" score-template="{value}" />
                </div>
              </div>
            </div>
          </el-card>
        </el-col>

        <el-col :xs="24" :md="8" class="mb-3">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-alert-outline"></i> {{ $t('dashboard.home.sections.inventoryAlerts') }}</h3>
              <el-badge :value="inventoryAlerts.length" />
            </div>
            <div class="inventory-alerts">
              <div
                v-for="(alert, idx) in inventoryAlerts"
                :key="alert.id + '-' + idx"
                class="alert-item"
                :class="alert.type"
              >
                <div class="alert-icon"><i :class="getAlertIcon(alert.type)" /></div>
                <div class="alert-body">
                  <div class="alert-title">{{ alert.item }}</div>
                  <div class="alert-msg">{{ alert.message }}</div>
                </div>
                <el-button size="mini" type="text" @click="handleAlertAction(alert)">{{ $t('dashboard.home.actions.fix') }}</el-button>
              </div>
            </div>
            <div class="view-all-link">
              <router-link to="/stock">{{ $t('dashboard.home.actions.manageInventory') }} <i class="el-icon-arrow-right" /></router-link>
            </div>
          </el-card>
        </el-col>

        <el-col :xs="24" :md="8" class="mb-3">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-history"></i> {{ $t('dashboard.home.sections.recentActivity') }}</h3>
            </div>
            <el-timeline>
              <el-timeline-item
                v-for="(act, i) in recentActivity"
                :key="i"
                :icon="act.icon"
                :type="act.type"
                :timestamp="act.time"
                placement="top"
              >{{ act.content }}</el-timeline-item>
            </el-timeline>
          </el-card>
        </el-col>
      </el-row>

      <!-- ── Row 5 · Sales Comparison full-width bar chart ── -->
      <el-row :gutter="20" class="mb-4">
        <el-col :xs="24">
          <el-card>
            <div slot="header" class="card-header">
              <h3><i class="mdi mdi-chart-bar"></i> {{ $t('dashboard.home.sections.salesComparison') }}</h3>
              <el-button-group size="mini">
                <el-button :type="comparisonView === 'daily'   ? 'primary' : ''" @click="changeComparisonView('daily')">{{ $t('dashboard.home.salesView.daily') }}</el-button>
                <el-button :type="comparisonView === 'weekly'  ? 'primary' : ''" @click="changeComparisonView('weekly')">{{ $t('dashboard.home.salesView.weekly') }}</el-button>
                <el-button :type="comparisonView === 'monthly' ? 'primary' : ''" @click="changeComparisonView('monthly')">{{ $t('dashboard.home.salesView.monthly') }}</el-button>
              </el-button-group>
            </div>
            <div class="chart-container" v-loading="comparisonLoading">
              <bar-chart :chart-data="salesComparisonData" :chart-options="comparisonChartOptions" :height="280" />
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- ── Footer ── -->
    <div class="dashboard-footer">
      <span class="last-update"><i class="el-icon-time" /> {{ $t('dashboard.home.lastUpdated') }}: {{ lastUpdateTime }}</span>
      <span v-if="nextUpdateIn > 0" class="next-update">{{ $t('dashboard.home.nextUpdateIn') }}: {{ nextUpdateIn }}s</span>
    </div>
  </div>
</template>

<script>
import BarChart from '../_Partials/Charts/BarChart'
import PieChart from '../_Partials/Charts/PieChart'
import DoughnutChart from '../_Partials/Charts/DoughnutChart'
import LineChart from '../_Partials/Charts/LineChart'
import DashboardRobustService from '@/services/dashboard.robust.service'
import CurrencyMixin from '@/mixins/CurrencyMixin'
import { mapState } from 'vuex'

export default {
  name: 'HomeNew',
  components: {
    BarChart, PieChart, DoughnutChart, LineChart
  },
  mixins: [CurrencyMixin],
  data () {
    return {
      loading: false,
      trendsLoading: false,
      comparisonLoading: false,
      dateRange: [],
      selectedStore: 'all',
      salesViewType: 'daily',
      showCategoryValue: true,
      comparisonView: 'daily',
      lastUpdateTime: new Date().toLocaleString(),
      nextUpdateIn: 300, // 5 minutes
      refreshInterval: null,
      countdownInterval: null,

      // Dashboard data
      dashboardData: null,
      rawKpiMetrics: [],
      kpiCards: [],
      topProducts: [],
      categoryData: [],
      paymentMethods: [],
      topPerformers: [],
      inventoryAlerts: [],
      recentActivity: [],
      salesPerformance: {},
      quickStats: [],
      stores: [],

      // Date shortcuts
      dateShortcuts: [],

      // Sparkline options
      sparklineOptions: {
        responsive: true,
        maintainAspectRatio: false,
        elements: {
          line: { borderWidth: 2 },
          point: { radius: 0 }
        },
        scales: {
          x: { display: false },
          y: { display: false }
        },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false }
        }
      },

      // Sales Performance Data
      salesPerformanceData: {
        labels: ['6AM', '8AM', '10AM', '12PM', '2PM', '4PM', '6PM', '8PM', '10PM'],
        datasets: [{
          label: this.$t('dashboard.home.period.today'),
          data: [4500, 6200, 8900, 12400, 15800, 14200, 11800, 8900, 5200],
          borderColor: '#409EFF',
          backgroundColor: 'rgba(64, 158, 255, 0.1)',
          tension: 0.4
        }, {
          label: this.$t('dashboard.home.period.yesterday'),
          data: [3800, 5400, 7200, 10800, 13200, 12800, 10200, 7600, 4800],
          borderColor: '#E6A23C',
          backgroundColor: 'rgba(230, 162, 60, 0.1)',
          tension: 0.4
        }]
      },

      salesChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return this.formatCurrency(value)
              }.bind(this)
            }
          }
        }
      },

      // Category distribution data
      categoryDistributionData: {
        labels: [],
        datasets: [{
          data: [],
          backgroundColor: [],
          borderWidth: 2,
          borderColor: '#fff'
        }]
      },

      doughnutOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        }
      },

      // Sales comparison data
      salesComparisonData: {
        labels: [],
        datasets: []
      },

      comparisonChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return this.formatCurrency(value)
              }.bind(this)
            }
          }
        }
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.Auth.user,
      license: state => state.Auth.license
    })
  },
  watch: {
    '$i18n.locale' () {
      this.dateShortcuts = this.getDateShortcuts()
      if (this.salesPerformanceData && this.salesPerformanceData.datasets) {
        if (this.salesPerformanceData.datasets[0]) this.salesPerformanceData.datasets[0].label = this.$t('dashboard.home.period.today')
        if (this.salesPerformanceData.datasets[1]) this.salesPerformanceData.datasets[1].label = this.$t('dashboard.home.period.yesterday')
      }
      this.kpiCards = this.buildKpiCards(this.rawKpiMetrics)
      this.quickStats = this.buildQuickStats((this.dashboardData && this.dashboardData.sales_summary) || {})
      this.updateSalesComparisonChart()
    }
  },
  async mounted () {
    // Set default date range to last 30 days
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 29)
    this.dateRange = [start, end]
    this.dateShortcuts = this.getDateShortcuts()

    await this.fetchStores()
    await this.fetchDashboardData()
    this.startAutoRefresh()
  },
  beforeDestroy () {
    this.stopAutoRefresh()
  },
  methods: {
    async fetchStores () {
      try {
        this.stores = await this.$departmentService.getDepartments()
      } catch (error) {
        this.$message.error(this.$t('dashboard.home.errors.failedToLoadStores'))
      }
    },

    async fetchDashboardData () {
      try {
        this.loading = true

        const filters = {
          start_date: this.dateRange[0],
          end_date: this.dateRange[1],
          store_ids: this.selectedStore !== 'all' ? [this.selectedStore] : undefined
        }

        const dashboardService = new DashboardRobustService(this.$http)
        const response = await dashboardService.getDashboardData(filters)

        if (response.status === 'success') {
          this.dashboardData = response.data
          this.updateDashboardComponents()
        } else {
          this.$message.error(this.$t('dashboard.home.errors.failedToFetchDashboardData'))
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
        this.$message.error(this.$t('dashboard.home.errors.errorLoadingDashboardData'))
      } finally {
        this.loading = false
        this.lastUpdateTime = new Date().toLocaleString()
        this.nextUpdateIn = 300
      }
    },

    updateDashboardComponents () {
      if (!this.dashboardData) return

      // KPI cards – store raw metrics so locale changes can re-translate titles
      this.rawKpiMetrics = this.dashboardData.kpi_metrics || []
      this.kpiCards = this.buildKpiCards(this.rawKpiMetrics)

      // Sales trends data → use for performance chart (daily view)
      if (this.dashboardData.sales_trends) {
        this.salesPerformanceData = this.dashboardData.sales_trends
      }

      // Sales performance stats derived from summary
      const summary = this.dashboardData.sales_summary || {}
      this.salesPerformance = {
        average_transaction: summary.average_order_value || 0,
        peak_hour: null,
        conversion_rate: null
      }

      // Top products – backend key matches
      this.topProducts = this.dashboardData.top_products || []

      // Category distribution – backend key matches
      this.categoryData = this.dashboardData.category_distribution || []
      this.updateCategoryChart()

      // Payment methods with icon mapping
      const iconMap = {
        cash: 'mdi mdi-cash',
        'mobile money': 'mdi mdi-cellphone-wireless',
        card: 'mdi mdi-credit-card-outline',
        check: 'mdi mdi-checkbook',
        credit: 'mdi mdi-bank-outline'
      }
      this.paymentMethods = (this.dashboardData.payment_methods || []).map(m => ({
        ...m,
        icon: iconMap[(m.name || '').toLowerCase()] || 'mdi mdi-credit-card-outline'
      }))

      // Employee performance – backend key is employee_performance, not top_performers
      this.topPerformers = (this.dashboardData.employee_performance || []).map(e => ({
        ...e,
        total_sales: e.sales,
        transactions: e.transactions,
        rating: Math.min(5, parseFloat((e.completion_rate / 20).toFixed(1)))
      }))

      // Inventory alerts – map type and warehouse→department for template
      this.inventoryAlerts = (this.dashboardData.inventory_alerts || []).map(alert => ({
        ...alert,
        type: alert.type === 'out_of_stock' ? 'critical' : 'warning',
        department: alert.warehouse
      }))

      // Recent activities – backend key is recent_activities (plural)
      this.recentActivity = this.dashboardData.recent_activities || []

      // Quick stats – derived from sales summary since no dedicated key in API
      this.quickStats = this.buildQuickStats(summary)

      // Sales comparison chart
      this.updateSalesComparisonChart()
    },

    buildKpiCards (metrics) {
      // Map server English titles → i18n keys so the cards react to locale changes
      const kpiTitleKeys = {
        'Total Sales': 'dashboard.home.kpi.totalSales',
        'Total Orders': 'dashboard.home.kpi.totalOrders',
        'Average Order': 'dashboard.home.kpi.averageOrder',
        'Profit Margin': 'dashboard.home.kpi.profitMargin'
      }
      return metrics.map(kpi => ({
        ...kpi,
        title: kpiTitleKeys[kpi.title] ? this.$t(kpiTitleKeys[kpi.title]) : kpi.title,
        sparklineData: Array.isArray(kpi.sparklineData)
          ? {
              labels: kpi.sparklineData.map((_, i) => i),
              datasets: [{
                label: '',
                data: kpi.sparklineData,
                borderColor: 'rgba(255,255,255,0.9)',
                backgroundColor: 'rgba(255,255,255,0.15)',
                tension: 0.4,
                pointRadius: 0,
                borderWidth: 2,
                fill: true
              }]
            }
          : (kpi.sparklineData || { labels: [], datasets: [] })
      }))
    },

    buildQuickStats (summary) {
      return [
        {
          title: this.$t('dashboard.home.quickStats.totalOrders'),
          value: (summary.total_orders || 0).toLocaleString(),
          icon: 'mdi mdi-receipt',
          color: 'primary',
          change: 0
        },
        {
          title: this.$t('dashboard.home.quickStats.outstandingBalance'),
          value: this.formatCurrency(summary.total_balance || 0),
          icon: 'mdi mdi-alert-circle-outline',
          color: 'warning',
          change: 0
        },
        {
          title: this.$t('dashboard.home.quickStats.totalDiscounts'),
          value: this.formatCurrency(summary.total_discount || 0),
          icon: 'mdi mdi-tag-outline',
          color: 'info',
          change: 0
        },
        {
          title: this.$t('dashboard.home.quickStats.grossProfit'),
          value: this.formatCurrency(summary.total_profit || 0),
          icon: 'mdi mdi-trending-up',
          color: 'success',
          change: 0
        }
      ]
    },

    updateCategoryChart () {
      this.categoryDistributionData.labels = this.categoryData.map(cat => cat.name)
      this.categoryDistributionData.datasets[0].data = this.categoryData.map(cat =>
        this.showCategoryValue ? cat.value : cat.units
      )
      this.categoryDistributionData.datasets[0].backgroundColor = this.categoryData.map(cat => cat.color)
    },

    updateSalesComparisonChart () {
      const trends = this.dashboardData.sales_trends
      if (trends && trends.labels && trends.datasets) {
        // Use revenue dataset only for the bar comparison chart
        const revenueDataset = trends.datasets.find(d => d.label === 'Revenue' || d.label === this.$t('dashboard.home.revenue')) || trends.datasets[0]
        this.salesComparisonData = {
          labels: trends.labels,
          datasets: [{
            label: this.$t('dashboard.home.revenue'),
            data: revenueDataset ? revenueDataset.data : [],
            backgroundColor: '#409EFF'
          }]
        }
      }
    },

    async changeComparisonView (view) {
      this.comparisonView = view
      this.comparisonLoading = true
      try {
        const dashboardService = new DashboardRobustService(this.$http)
        const filters = {
          start_date: this.dateRange[0],
          end_date: this.dateRange[1],
          granularity: view,
          store_ids: this.selectedStore !== 'all' ? [this.selectedStore] : undefined
        }
        const response = await dashboardService.getSalesTrends(filters)
        if (response.status === 'success') {
          const trends = response.data
          const revenueDataset = trends.datasets.find(d => d.label === 'Revenue') || trends.datasets[0]
          this.salesComparisonData = {
            labels: trends.labels,
            datasets: [{
              label: this.$t('dashboard.home.revenue'),
              data: revenueDataset ? revenueDataset.data : [],
              backgroundColor: '#409EFF'
            }]
          }
        }
      } catch (e) {
        this.$message.error(this.$t('dashboard.home.errors.failedToLoadComparisonData'))
      } finally {
        this.comparisonLoading = false
      }
    },

    getProgressColor (percentage) {
      if (percentage >= 80) return '#67C23A'
      if (percentage >= 60) return '#409EFF'
      if (percentage >= 40) return '#E6A23C'
      return '#F56C6C'
    },

    getAlertIcon (type) {
      const icons = {
        critical: 'mdi mdi-alert-circle',
        warning: 'mdi mdi-alert',
        info: 'mdi mdi-information'
      }
      return icons[type] || 'mdi mdi-alert'
    },

    async changeSalesView (view) {
      this.salesViewType = view
      this.trendsLoading = true
      try {
        const dashboardService = new DashboardRobustService(this.$http)
        const filters = {
          start_date: this.dateRange[0],
          end_date: this.dateRange[1],
          granularity: view,
          store_ids: this.selectedStore !== 'all' ? [this.selectedStore] : undefined
        }
        const response = await dashboardService.getSalesTrends(filters)
        if (response.status === 'success') {
          this.salesPerformanceData = response.data
        }
      } catch (e) {
        this.$message.error(this.$t('dashboard.home.errors.failedToLoadTrendData'))
      } finally {
        this.trendsLoading = false
      }
    },

    handleAlertAction (alert) {
      this.$message.info(this.$t('dashboard.home.messages.handlingAlertFor', { item: alert.item }))
      // Implement alert action logic
    },

    refreshDashboard () {
      this.fetchDashboardData()
    },

    getDateShortcuts () {
      return [{
        text: this.$t('dashboard.home.period.today'),
        value: () => {
          const end = new Date()
          const start = new Date()
          return [start, end]
        }
      }, {
        text: this.$t('dashboard.home.period.yesterday'),
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 1)
          end.setDate(end.getDate() - 1)
          return [start, end]
        }
      }, {
        text: this.$t('dashboard.home.period.last7Days'),
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 6)
          return [start, end]
        }
      }, {
        text: this.$t('dashboard.home.period.last30Days'),
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 29)
          return [start, end]
        }
      }, {
        text: this.$t('dashboard.home.period.thisMonth'),
        value: () => {
          const end = new Date()
          const start = new Date(end.getFullYear(), end.getMonth(), 1)
          return [start, end]
        }
      }, {
        text: this.$t('dashboard.home.period.lastMonth'),
        value: () => {
          const end = new Date()
          const start = new Date()
          start.setMonth(start.getMonth() - 1)
          start.setDate(1)
          end.setDate(0)
          return [start, end]
        }
      }]
    },

    getSalesViewLabel (view) {
      return this.$t(`dashboard.home.salesView.${view}`)
    },

    getPeriodText () {
      if (!this.dateRange || this.dateRange.length !== 2) return this.$t('dashboard.home.period.last30Days')

      const start = new Date(this.dateRange[0])
      const end = new Date(this.dateRange[1])
      const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24))

      if (daysDiff === 1) return this.$t('dashboard.home.period.today')
      if (daysDiff === 2) return this.$t('dashboard.home.period.yesterday')
      if (daysDiff <= 7) return this.$t('dashboard.home.period.last7Days')
      if (daysDiff <= 30) return this.$t('dashboard.home.period.last30Days')
      if (daysDiff <= 90) return this.$t('dashboard.home.period.last3Months')
      return this.$t('dashboard.home.period.custom')
    },

    startAutoRefresh () {
      // Refresh every 5 minutes
      this.refreshInterval = setInterval(() => {
        this.fetchDashboardData()
      }, 300000)

      // Countdown timer
      this.countdownInterval = setInterval(() => {
        if (this.nextUpdateIn > 0) {
          this.nextUpdateIn--
        }
      }, 1000)
    },

    stopAutoRefresh () {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
      }
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
      }
    }
  }
}
</script>

<style scoped>
/* ─── Layout ─────────────────────────────────── */
.dashboard-container {
  padding: 20px;
  background-color: #f0f2f7;
  min-height: 100vh;
}

.dashboard-header {
  background: #fff;
  padding: 18px 22px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07);
}

.header-info { line-height: 1.3; }

.dashboard-title {
  margin: 0;
  color: #1d2a4a;
  font-size: 22px;
  font-weight: 700;
}

.dashboard-subtitle {
  margin: 4px 0 0;
  color: #909399;
  font-size: 13px;
}

.header-filters {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
}

/* ─── Loading ─────────────────────────────────── */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-content {
  text-align: center;
  color: #909399;
}

.loading-content i {
  font-size: 36px;
  margin-bottom: 12px;
  display: block;
}

/* ─── KPI Gradient Cards ──────────────────────── */
.kpi-card {
  position: relative;
  overflow: hidden;
  border-radius: 14px;
  padding: 22px 20px 14px;
  min-height: 168px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 6px 20px rgba(0,0,0,0.14);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  cursor: default;
}

.kpi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 28px rgba(0,0,0,0.2);
}

.kpi-card.success {
  background: linear-gradient(135deg, #0f9b8e 0%, #4ade9e 100%);
}

.kpi-card.primary {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
}

.kpi-card.info {
  background: linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%);
}

.kpi-card.warning {
  background: linear-gradient(135deg, #d97706 0%, #fbbf24 100%);
}

/* Watermark icon — big, faded, top-right */
.kpi-watermark {
  position: absolute;
  top: -8px;
  right: -8px;
  font-size: 90px;
  line-height: 1;
  color: rgba(255,255,255,0.18);
  pointer-events: none;
  user-select: none;
}

/* Text content */
.kpi-body {
  position: relative;
  z-index: 1;
}

.kpi-label {
  font-size: 12px;
  color: rgba(255,255,255,0.82);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 6px;
}

.kpi-amount {
  font-size: 26px;
  font-weight: 800;
  color: #fff;
  line-height: 1.15;
  margin-bottom: 10px;
  word-break: break-all;
}

.kpi-trend-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.kpi-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
}

.kpi-badge.badge-up {
  background: rgba(255,255,255,0.28);
  color: #fff;
}

.kpi-badge.badge-down {
  background: rgba(0,0,0,0.22);
  color: rgba(255,255,255,0.9);
}

.kpi-vs {
  font-size: 11px;
  color: rgba(255,255,255,0.65);
}

/* Sparkline strip at the bottom of the card */
.kpi-spark {
  position: relative;
  z-index: 1;
  margin-top: 10px;
  height: 52px;
}

/* ─── Equal-height columns ────────────────────── */
/* el-row with type="flex" gets .el-row--flex (display:flex; flex-wrap:wrap)
   align-items defaults to stretch → all columns match the tallest */
.el-row--flex .el-col {
  display: flex;
  flex-direction: column;
}

/* Direct card children fill the stretched column */
.el-row--flex .el-col > .el-card,
.el-row--flex .el-col > .kpi-card {
  flex: 1;
}

/* ─── Section Cards ───────────────────────────── */
.full-height-card {
  height: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
  color: #1d2a4a;
  font-size: 15px;
  font-weight: 700;
}

/* ─── Charts ──────────────────────────────────── */
.chart-container {
  margin-bottom: 12px;
}

.chart-stats {
  display: flex;
  justify-content: space-around;
  padding-top: 14px;
  border-top: 1px solid #EBEEF5;
}

.stat-item {
  text-align: center;
}

.stat-label {
  display: block;
  color: #909399;
  font-size: 12px;
  margin-bottom: 4px;
}

.stat-value {
  color: #1d2a4a;
  font-weight: 700;
  font-size: 14px;
}

/* ─── Category legend ─────────────────────────── */
.category-legend {
  max-height: 200px;
  overflow-y: auto;
}

.legend-item {
  display: flex;
  align-items: center;
  padding: 7px 0;
  border-bottom: 1px solid #EBEEF5;
}

.legend-item:last-child {
  border-bottom: none;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 10px;
  flex-shrink: 0;
}

.legend-name {
  flex: 1;
  font-size: 13px;
  color: #303133;
}

.legend-value {
  font-weight: 700;
  color: #303133;
  font-size: 13px;
}

/* ─── Top Products ────────────────────────────── */
.product-list, .employee-list {
  max-height: 320px;
  overflow-y: auto;
}

.product-item, .employee-item {
  display: flex;
  align-items: center;
  padding: 11px 0;
  border-bottom: 1px solid #f0f2f7;
}

.product-item:last-child, .employee-item:last-child {
  border-bottom: none;
}

.product-rank, .employee-rank {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #409EFF;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  margin-right: 12px;
  flex-shrink: 0;
}

.product-rank.rank-1 { background: linear-gradient(135deg, #f7971e, #ffd200); }
.product-rank.rank-2 { background: linear-gradient(135deg, #b0bec5, #78909c); }
.product-rank.rank-3 { background: linear-gradient(135deg, #cd7f32, #a0522d); }

.product-info, .employee-info {
  flex: 1;
  min-width: 0;
}

.product-name, .employee-name {
  font-weight: 600;
  color: #303133;
  font-size: 13px;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.product-revenue {
  font-weight: 600;
  color: #4f46e5;
}

.trend-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 7px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  margin-left: 10px;
  flex-shrink: 0;
}

.trend-badge.up {
  background: #f0fff4;
  color: #22c55e;
}

.trend-badge.down {
  background: #fff1f1;
  color: #f56c6c;
}

/* ─── Payment Methods ─────────────────────────── */
.payment-methods {
  max-height: 320px;
  overflow-y: auto;
}

.payment-method {
  margin-bottom: 18px;
}

.payment-method:last-child {
  margin-bottom: 0;
}

.method-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.method-name {
  flex: 1;
  font-weight: 600;
  color: #303133;
  font-size: 13px;
}

.method-pct {
  font-weight: 700;
  color: #4f46e5;
  font-size: 13px;
}

.method-meta {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #909399;
  margin-top: 5px;
}

/* ─── Quick Stats ─────────────────────────────── */
.quick-stats {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.qstat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 14px;
  background: #f5f7fa;
  border-radius: 10px;
  transition: background 0.2s ease;
}

.qstat-card:hover {
  background: #eef0f5;
}

.qstat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #fff;
  flex-shrink: 0;
}

.qstat-icon.success  { background: linear-gradient(135deg, #0f9b8e, #4ade9e); }
.qstat-icon.primary  { background: linear-gradient(135deg, #4f46e5, #7c3aed); }
.qstat-icon.info     { background: linear-gradient(135deg, #0369a1, #0ea5e9); }
.qstat-icon.warning  { background: linear-gradient(135deg, #d97706, #fbbf24); }
.qstat-icon.danger   { background: linear-gradient(135deg, #dc2626, #f87171); }

.qstat-info { flex: 1; min-width: 0; }

.qstat-value {
  font-size: 18px;
  font-weight: 800;
  color: #1d2a4a;
  line-height: 1.2;
}

.qstat-label {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

/* ─── Employee / Performers ───────────────────── */
.employee-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

/* ─── Inventory Alerts ────────────────────────── */
.inventory-alerts {
  min-height: 300px;
  overflow-y: auto;
}

.alert-item {
  display: flex;
  align-items: flex-start;
  padding: 11px 12px;
  margin-bottom: 8px;
  border-radius: 8px;
  border-left: 4px solid;
}

.alert-item:last-child {
  margin-bottom: 0;
}

.alert-item.critical {
  background: #fff5f5;
  border-left-color: #f56c6c;
}

.alert-item.warning {
  background: #fffbeb;
  border-left-color: #f59e0b;
}

.alert-item.info {
  background: #eff6ff;
  border-left-color: #3b82f6;
}

.alert-icon {
  margin-right: 10px;
  font-size: 18px;
  padding-top: 2px;
}

.alert-item.critical .alert-icon { color: #f56c6c; }
.alert-item.warning  .alert-icon { color: #f59e0b; }
.alert-item.info     .alert-icon { color: #3b82f6; }

.alert-body {
  flex: 1;
  min-width: 0;
}

.alert-title {
  font-weight: 600;
  color: #303133;
  font-size: 13px;
  margin-bottom: 2px;
}

.alert-msg {
  font-size: 12px;
  color: #606266;
}

/* ─── Links ───────────────────────────────────── */
.view-all-link {
  text-align: center;
  padding-top: 12px;
  border-top: 1px solid #EBEEF5;
  margin-top: 8px;
}

.view-all-link a {
  color: #4f46e5;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
}

.view-all-link a:hover {
  text-decoration: underline;
}

/* ─── Footer ──────────────────────────────────── */
.dashboard-footer {
  text-align: center;
  padding: 18px;
  color: #909399;
  font-size: 12px;
}

.last-update {
  margin-right: 18px;
}

.next-update {
  color: #4f46e5;
}

/* ─── Responsive ──────────────────────────────── */
@media (max-width: 768px) {
  .dashboard-container { padding: 12px; }
  .dashboard-header    { padding: 14px 16px; }
  .dashboard-title     { font-size: 18px; }
  .kpi-amount          { font-size: 22px; }
  .chart-stats         { flex-direction: column; gap: 10px; }
  .stat-item           { text-align: left; }
  .header-filters      { justify-content: flex-start; }
}
</style>
